import { useState, useEffect, useMemo, useRef } from 'react'
import { Loader2, Check, X, Copy, Info } from 'lucide-react'
import { cloneApp, checkAppName, type CloneAppResponse, type NameCheckResult } from '../lib/api'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => any>(fn: T, ms: number) {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), ms)
  }
}

function sanitizeName(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50)
}

interface CloneAppModalProps {
  app: { name: string; display_name?: string | null }
  onClose: () => void
  onCloned: (cloneResponse: CloneAppResponse) => void
}

export default function CloneAppModal({ app, onClose, onCloned }: CloneAppModalProps) {
  const [cloneName, setCloneName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [nameCheck, setNameCheck] = useState<NameCheckResult | null>(null)
  const [checkingName, setCheckingName] = useState(false)
  const [cloning, setCloning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resolving, setResolving] = useState(true)

  const currentRequestRef = useRef<number>(0)

  // On mount: find first available name (copy-of-X, copy-of-X-2, copy-of-X-3, ...)
  useEffect(() => {
    let cancelled = false
    async function findAvailableName() {
      const base = `copy-of-${app.name}`
      const displayBase = app.display_name || app.name

      // Try base name first, then -2, -3, ...
      for (let i = 0; i < 20; i++) {
        const slugCandidate = i === 0 ? base : `${base}-${i + 1}`
        const displayCandidate = i === 0
          ? `Copy of ${displayBase}`
          : `Copy of ${displayBase} ${i + 1}`
        try {
          const result = await checkAppName(slugCandidate)
          if (cancelled) return
          if (result.available) {
            setCloneName(slugCandidate)
            setDisplayName(displayCandidate)
            setNameCheck(result)
            setResolving(false)
            return
          }
        } catch {
          if (cancelled) return
        }
      }
      // Fallback if all taken
      if (!cancelled) {
        setCloneName(`${base}-${Date.now().toString(36)}`)
        setDisplayName(`Copy of ${displayBase}`)
        setResolving(false)
      }
    }
    findAvailableName()
    return () => { cancelled = true }
  }, [app.name, app.display_name])

  const debouncedCheckName = useMemo(
    () => debounce(async (value: string) => {
      if (value.length < 3) {
        setNameCheck(null)
        return
      }

      const requestId = ++currentRequestRef.current
      setCheckingName(true)
      try {
        const result = await checkAppName(value)
        if (requestId === currentRequestRef.current) {
          setNameCheck(result)
        }
      } catch {
        if (requestId === currentRequestRef.current) {
          setNameCheck(null)
        }
      } finally {
        if (requestId === currentRequestRef.current) {
          setCheckingName(false)
        }
      }
    }, 300),
    []
  )

  useEffect(() => {
    if (resolving) return
    const sanitized = sanitizeName(cloneName)
    if (sanitized.length >= 3) {
      debouncedCheckName(sanitized)
    } else {
      setNameCheck(null)
    }
  }, [cloneName, debouncedCheckName, resolving])

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const sanitizedName = sanitizeName(cloneName)
  const isNameValid = sanitizedName.length >= 3 && nameCheck?.available === true

  async function handleClone(e: React.FormEvent) {
    e.preventDefault()
    if (!isNameValid) return

    setCloning(true)
    setError(null)

    try {
      const response = await cloneApp(app.name, {
        name: sanitizedName,
        display_name: displayName || undefined,
      })
      onCloned(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clone app')
      setCloning(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-card border border-border rounded-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Copy className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Clone App</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Create an independent copy of {app.display_name || app.name}
          </p>

          <form onSubmit={handleClone} className="space-y-4">
            {/* App Name (slug) */}
            <div>
              <label className="block text-sm font-medium mb-1.5">App Name (URL slug)</label>
              <div className="relative">
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  autoFocus
                  disabled={resolving}
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {resolving || checkingName ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : nameCheck?.available ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : nameCheck && !nameCheck.available ? (
                    <X className="w-4 h-4 text-destructive" />
                  ) : null}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">
                {nameCheck?.available === false ? (
                  <span className="text-destructive">
                    Name &quot;{sanitizedName}&quot; is taken. Try a different name.
                  </span>
                ) : sanitizedName.length >= 3 ? (
                  <>Your clone will be at <code className="bg-secondary px-1 rounded">/a/{sanitizedName}/</code></>
                ) : (
                  'Enter at least 3 characters'
                )}
              </p>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Optional display name"
                maxLength={60}
                disabled={resolving}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
            </div>

            {/* Info note */}
            <div className="flex gap-2.5 p-3 rounded-lg bg-blue-500/10 text-xs text-muted-foreground leading-relaxed">
              <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
              <p>
                Cloning copies code and settings.
                <span className="text-muted-foreground/70"> Database content is not included — use Claude in the workspace terminal to recreate it.</span>
              </p>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={cloning || !isNameValid || resolving}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {cloning ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cloning...
                  </>
                ) : (
                  'Clone'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
